"use client";

import { Button } from "@dafiti/closet-ui";
import { Chevron, Sorting } from "@dafiti/closet-ui/icons";
import { cn } from "@dafiti/closet-ui/utils";
import { useTranslations } from "next-intl";

import { useSortControlDesktop } from "../../../hooks/useSortControlDesktop.hook";
import type { SortControlProps } from "../../../types/sortControl.type";
import { SortOptionList } from "./SortOptionList";

export function SortControlDesktop({ sorting }: SortControlProps) {
  const t = useTranslations("catalog");
  const {
    localSelection,
    setLocalSelection,
    selectedLabel,
    isBoxOpen,
    boxRef,
    triggerRef,
    boxId,
    maxHeight,
    placement = "bottom",
    handleSelect,
    handleBoxToggle,
  } = useSortControlDesktop(sorting);

  return (
    <div className="relative hidden lg:flex justify-center">
      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        onClick={handleBoxToggle}
        aria-expanded={isBoxOpen}
        aria-controls={boxId}
        aria-label={t("sort.sortBy")}
        className={cn(
          "flex items-center gap-3 px-2 py-1 rounded-lg!",
          isBoxOpen && "border-support-100 hover:border-support-100",
        )}
        data-testid="sort-control-desktop-trigger"
      >
        <div className="w-8 h-8 flex items-center justify-center">
          <Sorting className="size-3.5" />
        </div>

        <span className="text-xs">{selectedLabel}</span>

        <div className="w-8 h-8 flex items-center justify-center">
          <Chevron
            className={cn(
              "size-3 transition-transform rotate-180",
              isBoxOpen && "rotate-0",
            )}
          />
        </div>
      </Button>

      {isBoxOpen && (
        <div
          ref={boxRef}
          id={boxId}
          className={cn(
            "absolute left-1/2 -translate-x-1/2 z-50 min-w-[280px] overflow-y-auto rounded-lg border border-support-100 bg-white shadow-[0_8px_16px_0_rgba(0,0,0,0.16)] px-4 py-2",
            placement === "bottom" ? "top-full mt-2" : "bottom-full mb-2",
          )}
          style={{ maxHeight: maxHeight ? `${maxHeight}px` : undefined }}
          data-testid="sort-control-desktop-box"
        >
          <SortOptionList
            options={sorting}
            currentValue={localSelection}
            onSelect={setLocalSelection}
            onConfirmSelect={handleSelect}
          />
        </div>
      )}
    </div>
  );
}
